import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel
} from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import * as FlagIcons from "country-flag-icons/react/3x2";

type ActivityEntryResponse = {
  id?: number;
  timestamp?: string;
  username?: string;
  clientId?: string;
  event?: string;
  city?: string;
  country?: string;
  ip?: string;
  browser?: string;
};

type ActivityResponse = {
  entries?: ActivityEntryResponse[];
  page?: number;
  pageSize?: number;
  totalEntries?: number;
  totalPages?: number;
  total?: number;
};

type ActivityRow = {
  id: number;
  timestamp: string;
  username: string;
  clientId: string;
  event: string;
  city: string;
  country: string;
  ip: string;
  browser: string;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200] as const;
const ACTIVITY_PAGE_SIZE_STORAGE_KEY = "user-activity-page-size";
const ACTIVITY_DOWNLOAD_PAGE_SIZE = 500;

const getStoredPageSize = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const storedValue = window.localStorage.getItem(ACTIVITY_PAGE_SIZE_STORAGE_KEY);
  const parsedValue = storedValue ? Number.parseInt(storedValue, 10) : NaN;
  if (Number.isNaN(parsedValue)) {
    return undefined;
  }
  const options = Array.from(PAGE_SIZE_OPTIONS);
  return options.includes(parsedValue as (typeof PAGE_SIZE_OPTIONS)[number])
    ? (parsedValue as (typeof PAGE_SIZE_OPTIONS)[number])
    : undefined;
};

const persistPageSize = (pageSize: number) => {
  if (typeof window === "undefined") {
    return;
  }
  const options = Array.from(PAGE_SIZE_OPTIONS);
  if (options.includes(pageSize as (typeof PAGE_SIZE_OPTIONS)[number])) {
    window.localStorage.setItem(ACTIVITY_PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }
};

const escapeCsv = (value: string) => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, "\"\"")}"`;
  }
  return value;
};

const downloadCsv = (fileName: string, csv: string) => {
  if (typeof window === "undefined") {
    return;
  }
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const UserActivityTable = () => {
  const { authorizedFetch } = useAuth();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() => {
    const storedPageSize = getStoredPageSize();
    return {
      page: 0,
      pageSize: storedPageSize ?? 50
    };
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "timestamp", sort: "desc" }
  ]);
  const [searchText, setSearchText] = useState("");
  const normalizedSearch = useMemo(() => searchText.trim(), [searchText]);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useQuery<{
    entries: ActivityRow[];
    page: number;
    pageSize: number;
    totalEntries: number;
    totalPages: number;
  }>({
    queryKey: ["user-activity", paginationModel.page, paginationModel.pageSize, normalizedSearch.toLowerCase()],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(paginationModel.page + 1),
        pageSize: String(paginationModel.pageSize)
      });
      if (normalizedSearch) {
        params.set("query", normalizedSearch);
      }
      const response = await authorizedFetch(`/api/activity?${params.toString()}`);
      const payload = (await response.json().catch(() => null)) as (ActivityResponse & { error?: string }) | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load user activity");
      }

      const page = typeof payload?.page === "number" && payload.page > 0 ? payload.page : 1;
      const pageSize =
        typeof payload?.pageSize === "number" && payload.pageSize > 0 ? payload.pageSize : paginationModel.pageSize;

      const entries = Array.isArray(payload?.entries) ? payload.entries : [];
      const rows: ActivityRow[] = entries.map((entry, index) => ({
        id:
          typeof entry?.id === "number" && !Number.isNaN(entry.id)
            ? entry.id
            : (page - 1) * pageSize + index + 1,
        timestamp: typeof entry?.timestamp === "string" ? entry.timestamp : "",
        username: typeof entry?.username === "string" ? entry.username : "",
        clientId: typeof entry?.clientId === "string" ? entry.clientId : "",
        event: typeof entry?.event === "string" ? entry.event : "",
        city: typeof entry?.city === "string" ? entry.city : "",
        country: typeof entry?.country === "string" ? entry.country : "",
        ip: typeof entry?.ip === "string" ? entry.ip : "",
        browser: typeof entry?.browser === "string" ? entry.browser : ""
      }));

      const totalEntries =
        typeof payload?.totalEntries === "number" && payload.totalEntries >= 0
          ? payload.totalEntries
          : typeof payload?.total === "number" && payload.total >= 0
            ? payload.total
            : rows.length;
      const totalPages =
        typeof payload?.totalPages === "number" && payload.totalPages >= 0
          ? payload.totalPages
          : totalEntries > 0
            ? Math.ceil(totalEntries / pageSize)
            : 0;

      return {
        entries: rows,
        page,
        pageSize,
        totalEntries,
        totalPages
      };
    },
    placeholderData: (previous) => previous
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const pageSizes = Array.from(PAGE_SIZE_OPTIONS);
    setPaginationModel((previous) => {
      const serverPage = Math.max(data.page - 1, 0);
      const serverPageSize = data.pageSize > 0 ? data.pageSize : previous.pageSize;
      const normalizedPageSize = pageSizes.includes(serverPageSize as (typeof PAGE_SIZE_OPTIONS)[number])
        ? serverPageSize
        : previous.pageSize;
      if (previous.page === serverPage && previous.pageSize === normalizedPageSize) {
        return previous;
      }
      return {
        page: serverPage,
        pageSize: normalizedPageSize
      };
    });
  }, [data]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel((previous) => {
      const nextPageSize = typeof model.pageSize === "number" && model.pageSize > 0 ? model.pageSize : previous.pageSize;
      const pageChanged = typeof model.page === "number" ? model.page : previous.page;
      const isPageSizeChange = nextPageSize !== previous.pageSize;
      return {
        page: isPageSizeChange ? 0 : pageChanged,
        pageSize: nextPageSize
      };
    });
  };

  useEffect(() => {
    setPaginationModel((previous) => (previous.page === 0 ? previous : { ...previous, page: 0 }));
  }, [normalizedSearch]);

  const handleOpenClearDialog = useCallback(() => {
    setClearError("");
    setIsClearDialogOpen(true);
  }, []);

  const handleCloseClearDialog = useCallback(() => {
    if (isClearing) return;
    setIsClearDialogOpen(false);
  }, [isClearing]);

  const handleConfirmClear = useCallback(async () => {
    setClearError("");
    setIsClearing(true);
    try {
      const response = await authorizedFetch("/api/activity", { method: "DELETE" });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? "Unable to clear activity");
      }
      setIsClearDialogOpen(false);
      await refetch();
    } catch (caughtError) {
      if (caughtError instanceof Error) {
        setClearError(caughtError.message);
      } else if (typeof caughtError === "string") {
        setClearError(caughtError);
      } else {
        setClearError("Unable to clear activity");
      }
    } finally {
      setIsClearing(false);
    }
  }, [authorizedFetch, refetch, isClearing]);

  const handleDownloadActivity = useCallback(async () => {
    setDownloadError("");
    setIsDownloading(true);
    try {
      let page = 1;
      let totalPages = 1;
      const allRows: ActivityRow[] = [];

      while (page <= totalPages) {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(ACTIVITY_DOWNLOAD_PAGE_SIZE)
        });
        if (normalizedSearch) {
          params.set("query", normalizedSearch);
        }

        const response = await authorizedFetch(`/api/activity?${params.toString()}`);
        const payload = (await response.json().catch(() => null)) as (ActivityResponse & { error?: string }) | null;
        if (!response.ok) {
          throw new Error(payload?.error ?? "Unable to download user activity");
        }

        const serverPage = typeof payload?.page === "number" && payload.page > 0 ? payload.page : page;
        const serverPageSize =
          typeof payload?.pageSize === "number" && payload.pageSize > 0 ? payload.pageSize : ACTIVITY_DOWNLOAD_PAGE_SIZE;
        const entries = Array.isArray(payload?.entries) ? payload.entries : [];
        entries.forEach((entry, index) => {
          allRows.push({
            id:
              typeof entry?.id === "number" && !Number.isNaN(entry.id)
                ? entry.id
                : (serverPage - 1) * serverPageSize + index + 1,
            timestamp: typeof entry?.timestamp === "string" ? entry.timestamp : "",
            username: typeof entry?.username === "string" ? entry.username : "",
            clientId: typeof entry?.clientId === "string" ? entry.clientId : "",
            event: typeof entry?.event === "string" ? entry.event : "",
            city: typeof entry?.city === "string" ? entry.city : "",
            country: typeof entry?.country === "string" ? entry.country : "",
            ip: typeof entry?.ip === "string" ? entry.ip : "",
            browser: typeof entry?.browser === "string" ? entry.browser : ""
          });
        });

        totalPages = typeof payload?.totalPages === "number" && payload.totalPages > 0 ? payload.totalPages : 1;
        page += 1;
      }

      const header = ["id", "timestamp", "username", "clientId", "event", "city", "country", "ip", "browser"];
      const lines = allRows.map((row) =>
        [
          String(row.id),
          row.timestamp,
          row.username,
          row.clientId,
          row.event,
          row.city,
          row.country,
          row.ip,
          row.browser
        ]
          .map((value) => escapeCsv(value))
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const fileStamp = new Date().toISOString().replace(/[:.]/g, "-");
      downloadCsv(`skyview-user-activity-${fileStamp}.csv`, csv);
    } catch (caughtError) {
      if (caughtError instanceof Error) {
        setDownloadError(caughtError.message);
      } else if (typeof caughtError === "string") {
        setDownloadError(caughtError);
      } else {
        setDownloadError("Unable to download user activity");
      }
    } finally {
      setIsDownloading(false);
    }
  }, [authorizedFetch, normalizedSearch]);

  const columns = useMemo<GridColDef<ActivityRow>[]>(() => {
    const formatTimestamp = (value: string) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value || "-";
      }
      return date.toLocaleString(undefined, { hourCycle: "h23" });
    };

    const summarizeBrowser = (value: string) => {
      const ua = value || "";
      const normalized = ua.toLowerCase();
      if (!normalized) return "";
      if (normalized.includes("edg")) return "Edge";
      if (normalized.includes("opr") || normalized.includes("opera")) return "Opera";
      if (normalized.includes("firefox")) return "Firefox";
      if (normalized.includes("chrome")) return "Chrome";
      if (normalized.includes("safari")) return "Safari";
      const firstToken = ua.split(" ")[0];
      if (firstToken) return firstToken;
      return ua.slice(0, 20);
    };

    const regionNames =
      typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : undefined;

    return [
      {
        field: "timestamp",
        headerName: "Time",
        width: 210,
        minWidth: 210,
        resizable: true,
        valueGetter: (value, row) => row?.timestamp ?? "",
        renderCell: (params) => {
          const val = params.row.timestamp?.trim();
          if (!val) {
            return "-";
          }
          return (
            <Tooltip title={val} placement="top-start" arrow>
              <Typography variant="body2" noWrap>{formatTimestamp(val)}</Typography>
            </Tooltip>
          );
        }
      },
      { field: "username", headerName: "User", minWidth: 140, flex: 0.4, resizable: true },
      {
        field: "event",
        headerName: "Event",
        width: 190,
        minWidth: 190,
        resizable: true,
        renderCell: (params) => {
          const value = params.row.event?.trim() || "-";
          return (
            <Tooltip title={value} placement="top-start" arrow>
              <Typography variant="body2" noWrap>{value}</Typography>
            </Tooltip>
          );
        }
      },
      { field: "clientId", headerName: "Client ID", minWidth: 160, flex: 0.6, resizable: true },
      { field: "city", headerName: "City", minWidth: 120, flex: 0.35, resizable: true },
      {
        field: "country",
        headerName: "Country",
        minWidth: 140,
        flex: 0.4,
        resizable: true,
        renderCell: (params) => {
          const code = params.row.country?.trim().toUpperCase();
          const Flag = code ? (FlagIcons as Record<string, React.FC<{ title?: string }>>)[code] : undefined;
          const name = code ? regionNames?.of(code) ?? code : "-";
          return (
            <Tooltip title={code || ""} placement="top-start" arrow>
              <Stack direction="row" spacing={1} alignItems="center">
                {Flag ? (
                  <Box
                    sx={{
                      width: 24,
                      height: 16,
                      borderRadius: 1,
                      border: "1px solid rgba(0,0,0,0.1)",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Flag title={code} />
                  </Box>
                ) : (
                  <Box sx={{ width: 24, height: 16 }} />
                )}
                <Typography variant="body2">{name}</Typography>
              </Stack>
            </Tooltip>
          );
        }
      },
      { field: "ip", headerName: "IP", minWidth: 140, flex: 0.35, resizable: true },
      {
        field: "browser",
        headerName: "Browser",
        minWidth: 80,
        flex: 0.4,
        resizable: true,
        renderCell: (params) => {
          const value = params.row.browser?.trim();
          const shortValue = summarizeBrowser(value ?? "");
          return (
            <Tooltip title={value} placement="top-start" arrow>
              <Typography variant="body2">{shortValue || "-"}</Typography>
            </Tooltip>
          );
        }
      }
    ];
  }, []);

  const rows = data?.entries ?? [];
  const rowCount = data?.totalEntries ?? rows.length;

  useEffect(() => {
    persistPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize]);

  const errorMessage = useMemo(() => {
    if (!isError) {
      return "";
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "Unable to load user activity";
  }, [error, isError]);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ width: "100%" }}
        justifyContent="space-between"
      >
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search activity"
          size="small"
          sx={{ minWidth: { xs: "100%", sm: 240 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Stack direction="row" spacing={1} sx={{ width: "100%", justifyContent: "flex-end", ml: { sm: "auto" } }}>
          <Tooltip title="Download activity" arrow>
            <span>
              <IconButton
                aria-label="Download activity"
                color="primary"
                onClick={() => {
                  void handleDownloadActivity();
                }}
                disabled={isFetching || isDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              void refetch();
            }}
            disabled={isFetching}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleOpenClearDialog}
            disabled={isFetching || isClearing}
          >
            Clear Activity
          </Button>
        </Stack>
      </Stack>
      {clearError && <Alert severity="error">{clearError}</Alert>}
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      {isError && <Alert severity="error">{errorMessage}</Alert>}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 500,
          overflow: "hidden"
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationMode="server"
          sortingMode="client"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={Array.from(PAGE_SIZE_OPTIONS)}
          rowCount={rowCount}
          loading={isLoading || isFetching}
          disableRowSelectionOnClick
          disableColumnMenu
          getRowHeight={() => "auto"}
          sx={{
            flex: 1,
            border: 0,
            "& .MuiDataGrid-row": {
              maxHeight: "fit-content !important"
            },
            "& .MuiDataGrid-cell": {
              py: 1
            }
          }}
        />
      </Paper>
      <Dialog open={isClearDialogOpen} onClose={handleCloseClearDialog}>
        <DialogTitle>Clear activity?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes all recorded user activity entries. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearDialog} disabled={isClearing}>
            Cancel
          </Button>
          <Button onClick={() => { void handleConfirmClear(); }} color="error" disabled={isClearing} autoFocus>
            {isClearing ? "Clearing..." : "Clear activity"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserActivityTable;
